import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("new");
  const [priceMax, setPriceMax] = useState(50000);
  const [onlyDeals, setOnlyDeals] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, q]);

  const filtered = products.filter((p) => {
    const finalPrice = Number(p.price) * (1 - Number(p.discount_percent || 0) / 100);
    if (finalPrice > priceMax) return false;
    if (onlyDeals && Number(p.discount_percent || 0) <= 0) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    if (sort === "discount") return (b.discount_percent || 0) - (a.discount_percent || 0);
    return 0;
  });

  const title = q ? `Results for "${q}"` : category ? category[0].toUpperCase() + category.slice(1) : "All Products";

  return (
    <div className="min-h-screen" data-testid="shop-page">
      <div className="max-w-[1500px] mx-auto px-3 md:px-6 py-6">
        <div className="card p-4 flex items-center justify-between text-sm">
          <div>
            <span className="text-[#6b6b6b]">{sorted.length} results</span>
            <span className="mx-2 text-[#6b6b6b]">·</span>
            <span className="font-display font-semibold text-[#1a1d29]">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#6b6b6b] hidden md:inline">Sort by:</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-[#e0d8c8] rounded-md h-10 px-3 text-sm bg-white outline-none focus:border-[#c8532e]"
              data-testid="shop-sort-select"
            >
              <option value="new">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="discount">Biggest Discount</option>
            </select>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-12 gap-4">
          <aside className="hidden md:block col-span-3 lg:col-span-2">
            <div className="card p-4 sticky top-[130px]">
              <div className="font-display font-semibold text-[#1a1d29] mb-3">Department</div>
              <ul className="space-y-2 text-sm">
                <li><a href="/shop" className="ec-link">All</a></li>
                <li><a href="/shop/women" className="ec-link">Women</a></li>
                <li><a href="/shop/men" className="ec-link">Men</a></li>
                <li><a href="/shop/accessories" className="ec-link">Accessories</a></li>
              </ul>

              <div className="mt-6 font-display font-semibold text-[#1a1d29] mb-3">Price</div>
              <div className="text-sm">
                <div className="flex justify-between text-xs text-[#6b6b6b] mb-1">
                  <span>₹0</span>
                  <span>₹{priceMax.toLocaleString("en-IN")}+</span>
                </div>
                <input
                  type="range"
                  min="500"
                  max="50000"
                  step="500"
                  value={priceMax}
                  onChange={(e) => setPriceMax(Number(e.target.value))}
                  className="w-full accent-[#c8532e]"
                  data-testid="shop-price-slider"
                />
              </div>

              <div className="mt-6 font-display font-semibold text-[#1a1d29] mb-3">Deals</div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={onlyDeals}
                  onChange={(e) => setOnlyDeals(e.target.checked)}
                  className="w-4 h-4 accent-[#c8532e]"
                  data-testid="shop-deals-only"
                />
                On sale only
              </label>
            </div>
          </aside>

          <main className="col-span-12 md:col-span-9 lg:col-span-10">
            {loading ? (
              <div className="card p-8 text-center text-sm text-[#6b6b6b]">Loading products…</div>
            ) : sorted.length === 0 ? (
              <div className="card p-12 text-center" data-testid="shop-empty">
                <div className="font-display text-xl font-semibold">No results found</div>
                <div className="text-sm text-[#6b6b6b] mt-2">Try adjusting your filters or search another term.</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {sorted.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
